import React, { useContext, useEffect, useState } from 'react';
import { func } from 'prop-types';
import { useFormState } from 'react-use-form-state';
import { Banner } from '@shoflo/polymer/components/Banner/Banner';
import { Button } from '@shoflo/polymer/components/Button/Button';
import BrandContext from '@shoflo/polymer/contexts/BrandContext';
import { updateBannerFont } from '@studio/program/utils/formatBannerMarkup';
import { Form, Row, Col } from 'reactstrap';
import BannerLayerSourceFormHexColorInput from '../HexColorInput';
import FieldTypes from '../FieldTypes';
import getFieldValidator from '../fieldValidatorFactory';
import {
  BannerWrapper,
  StyledCol,
  StyledContainer,
  StyledFormGroup,
  StyledRadioLabel,
  StyledCancelButton
} from './PresenterBannerLayerSourceForm.styled';
import BannerTemplateListPropType from 'components/BannerTemplateListPropType';
import LayerSourcePropType from 'components/LayerSourcePropType';

const DEFAULT_COLOR = '#FFFFFF';

const PresenterBannerLayerSourceForm = ({
  onCancel,
  onSubmit,
  banner,
  templates
}) => {
  const brandContext = useContext(BrandContext);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const source = banner?.source;

  const [isUnEdited, setIsUnEdited] = useState(!!source);

  const [formState, { text }] = useFormState();

  const selectedTemplate = templates.find(
    ({ _id }) => _id === source?.value?.templateId
  );

  const field = selectedTemplate?.fields.find(
    ({ type }) => type === FieldTypes.HexColor
  );

  const statefulFields = {
    title: source?.title || ''
  };

  const initialAccentColor = source?.value.templateVars['useAccentAsBackground']
    ? source?.value.templateVars[field.key]
    : DEFAULT_COLOR;

  useEffect(() => {
    formState.setField(field?.label, initialAccentColor);
  }, [source, field, formState, initialAccentColor]);

  useEffect(() => {
    if (isUnEdited) {
      Object.keys(statefulFields).forEach(key => {
        formState.setField(key, statefulFields[key]);
      });
      setIsUnEdited(false);
    }
  }, [isUnEdited, statefulFields, formState]);

  if (!source) return null;

  const initialTitle = source?.title;

  const disableSubmitButton = () => {
    return (
      (initialAccentColor === formState.values[field.label] &&
        initialTitle === formState.values.title) ||
      isSubmitting
    );
  };

  const inputProps = {
    invalid: formState.touched[field.label] && !formState.validity[field.label],
    ...text({
      name: field.label,
      validate: getFieldValidator(field),
      validateOnBlur: false,
      touchOnChange: true
    })
  };

  const handleSubmit = event => {
    event.preventDefault();
    setIsSubmitting(true);
    let templateVars = {
      ...source.value.templateVars,
      [field.key]: formState.values[field.label],
      useAccentAsBackground: true
    };

    const layerSource = {
      source: {
        title: formState.values.title,
        value: {
          ...source.value,
          templateVars
        }
      }
    };

    onSubmit(layerSource);
  };

  let markup = source.value.markup.replace(
    initialAccentColor,
    formState.values[field.label]
  );

  markup = updateBannerFont(markup, brandContext?.fonts);

  return (
    <div>
      <Form data-test="presenterBannerLayerSourceForm" onSubmit={handleSubmit}>
        <StyledContainer>
          <Row>
            <StyledCol sm={3}>
              <div className="radio-container">
                <input type="radio" checked={true} readOnly />
                <StyledRadioLabel for="radio-template-chooser"></StyledRadioLabel>
              </div>
            </StyledCol>
            <Col sm={9}>
              <BannerWrapper className="banner">
                <Banner>{markup}</Banner>
              </BannerWrapper>
            </Col>
          </Row>
        </StyledContainer>
        <StyledFormGroup>
          <BannerLayerSourceFormHexColorInput
            field={field}
            formState={formState}
            inputProps={inputProps}
          />
        </StyledFormGroup>

        <div className="float-end">
          <Button data-testid="submitButton" disabled={disableSubmitButton()}>
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>{' '}
          <StyledCancelButton
            data-test="cancelButton"
            color="light"
            variant="ghost"
            onClick={e => {
              e.preventDefault();
              onCancel();
            }}
          >
            Cancel
          </StyledCancelButton>
        </div>
      </Form>
    </div>
  );
};

PresenterBannerLayerSourceForm.propTypes = {
  templates: BannerTemplateListPropType,
  banner: LayerSourcePropType,
  onSubmit: func.isRequired,
  onCancel: func.isRequired
};

export default PresenterBannerLayerSourceForm;
